import { useMutation, useQueryClient } from "@tanstack/react-query";
import { exonnHubProductQueries } from "src/admin/modules/b2b-gate-order/components/ExonnHub/queries.ts";
import type { GetCatalog } from "@exonn/js-sdk";
import { useSnackbar } from "src/App/App.hooks.ts";
import { useExonnApi } from "src/exonn-api/exonn-api.tsx";
import { arrReplaceAt } from "@exonn/utils";

export const useRemoveProductFromFavourite = (
  params: GetCatalog.QueryParameters,
) => {
  const queryClient = useQueryClient();
  const api = useExonnApi();
  const snackbar = useSnackbar();
  return useMutation({
    mutationFn: async ({ variant_id }: { variant_id: string }) => {
      return (
        await api.supplierProductHub.removeProductFromFavouriteExonnHub(null, {
          variant_id: variant_id,
        })
      ).data;
    },
    onMutate: async ({ variant_id }) => {
      const listQuery = exonnHubProductQueries.list(params);
      await queryClient.cancelQueries(listQuery);

      const previousList = queryClient.getQueryData(listQuery.queryKey);
      queryClient.setQueryData(
        exonnHubProductQueries.list(params).queryKey,
        removeExonnHubProductFromFavoriteUpdater(variant_id),
      );

      return {
        previousList: previousList,
      };
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(
        exonnHubProductQueries.list(params).queryKey,
        context?.previousList,
      );
      snackbar.error({
        message: "Couldn't remove product from favorites",
      });
    },
  });
};

const removeExonnHubProductFromFavoriteUpdater = (variant_id: string) => {
  return (prev: GetCatalog.Responses.$200 | undefined) => {
    if (prev?.data) {
      const data = prev.data;
      const targetIndex = data.findIndex(
        (product) => product.id === variant_id,
      );
      const target = data[targetIndex];
      return {
        ...prev,
        data: arrReplaceAt(data, targetIndex, {
          ...target,
          is_favourite: false,
        }),
      };
    }
    return prev;
  };
};
